import {
	Component,
	OnInit,
	Input,
	Output,
	ViewChild,
	EventEmitter,
	QueryList,
	ViewChildren,
} from '@angular/core';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { SelectionModel } from '@angular/cdk/collections';
import { MtxPopoverTrigger } from '@ng-matero/extensions/popover';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormArray } from '@angular/forms';
import { UtilService } from '@service/util/util.service';
import { IS_NULL_OR_UNDEFINED } from 'cargo-chief-commons';

@Component({
	selector: 'app-carrier-filter',
	templateUrl: './carrier-filter.component.html',
	styleUrls: ['./carrier-filter.component.scss'],
})
export class CarrierFilterComponent implements OnInit {
	@Input() matchedCarriers: any;
	@Input() oldSelection: any;
	@Output() switchToLoadDetails: EventEmitter<any> = new EventEmitter<String>();
	@ViewChildren(MtxPopoverTrigger) triggers: QueryList<MtxPopoverTrigger>;
	@ViewChild(MatPaginator) paginator: MatPaginator;

	PAGE_SIZE = 15;

	isAddConditionActive: boolean = false;
	isFilterActive: boolean = false;
	filteredCarriers = Array<any>();
	dataSource = new MatTableDataSource<any>([]);
	selection: SelectionModel<any>;
	carrierFilterForm: UntypedFormGroup;
	displayedColumns = ['select', 'business_name', 'firm_mc', 'firm_dot'];
	filterFieldMap: any = {
		Name: 'businessName',
		MC: 'firmMc',
		DOT: 'firmDot',
	};
	availableFields: string[] = ['Name', 'MC', 'DOT'];

	IS_NULL_OR_UNDEFINED = IS_NULL_OR_UNDEFINED;

	constructor(private formBuilder: UntypedFormBuilder, private utilService: UtilService) {}

	ngOnInit(): void {
		this.dataSource.paginator = this.paginator;
		this.filteredCarriers = this.matchedCarriers ? [...this.matchedCarriers] : [];

		if (this.filteredCarriers?.length > 0) {
			this.dataSource.data = this.filteredCarriers.slice(0, this.PAGE_SIZE);
			if (this.oldSelection.selected.length) {
				const selectedCarrierIds = this.oldSelection.selected.map((carrier) => carrier.carrierId);
				this.selection = new SelectionModel<any>(
					true,
					this.filteredCarriers.filter(
						(carrier) => selectedCarrierIds.indexOf(carrier.carrierId) !== -1
					)
				);
			}
		}

		this.carrierFilterForm = this.formBuilder.group({
			formArray: this.formBuilder.array([
				this.formBuilder.group({
					filterField: '',
					filterText: '',
				}),
			]),
		});

		this.carrierFilterForm.valueChanges.subscribe((val) => {
			this.isAddConditionActive = this.hasMoreCondition();
			this.isFilterActive = this.hasAnyFilter();
		});
	}

	ngOnChanges() {
		this.selection = this.oldSelection;
	}

	/** Whether the number of selected elements matches the total number of rows. */
	isAllSelected() {
		const numSelected = this.selection.selected.length;
		const numRows = this.matchedCarriers.length;
		return numSelected == numRows;
	}

	/** Selects all rows if they are not all selected; otherwise clear selection. */
	masterToggle() {
		this.isAllSelected()
			? this.selection.clear()
			: this.matchedCarriers?.forEach((row) => this.selection.select(row));
	}

	/** The label for the checkbox on the passed row */
	checkboxLabel(row?: any): string {
		if (!row) {
			return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
		}
		return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id + 1}`;
	}

	getCarriersForPage(pageEvent: any) {
		let startIndex = pageEvent.pageIndex * pageEvent.pageSize;
		let endIndex = startIndex + pageEvent.pageSize;
		if (endIndex > this.filteredCarriers.length) {
			endIndex = this.filteredCarriers.length;
		}
		this.dataSource.data = this.filteredCarriers.slice(startIndex, endIndex);
	}

	openLoadDetails() {
		this.switchToLoadDetails.emit();
	}

	excludeCarriers() {
		if (this.isAllSelected()) {
			this.triggers.toArray()[1].openPopover();
		} else {
			this.switchToLoadDetails.emit(this.selection);
		}
	}

	get formArray() {
		return this.carrierFilterForm.get('formArray') as UntypedFormArray;
	}

	addFormGroup() {
		this.formArray.push(
			this.formBuilder.group({
				filterField: '',
				filterText: '',
			})
		);
	}

	removeFormGroup(index: number) {
		this.formArray.removeAt(index);
	}

	hasMoreCondition(): boolean {
		if (this.formArray.length > this.availableFields.length - 1) {
			return false;
		}

		for (let i = 0; i < this.formArray.length; i++) {
			if (
				this.formArray.at(i).get('filterField').value.length < 1 ||
				this.formArray.at(i).get('filterText').value.length < 1
			) {
				return false;
			}
		}

		return true;
	}

	hasAnyFilter(): boolean {
		for (let i = 0; i < this.formArray.length; i++) {
			if (
				this.formArray.at(i).get('filterField').value.length > 0 &&
				this.formArray.at(i).get('filterText').value.length > 0
			) {
				return true;
			}
		}

		return false;
	}

	filterCarriers() {
		this.clearFilters();

		for (let i = 0; i < this.formArray.length; i++) {
			let filterField = this.formArray.at(i).get('filterField').value;
			let filterText = this.formArray.at(i).get('filterText').value.trim().toLowerCase();

			if (filterField.length > 0 && filterText.length > 0) {
				this.filteredCarriers = this.filteredCarriers.filter(
					(carrier) =>
						carrier[this.filterFieldMap[filterField]]?.trim()?.toLowerCase().indexOf(filterText) >
						-1
				);
			}
		}
		this.dataSource.data = this.filteredCarriers.slice(0, this.PAGE_SIZE);
	}

	clearFilters() {
		this.filteredCarriers = this.matchedCarriers ? [...this.matchedCarriers] : [];
		this.dataSource.data = this.matchedCarriers?.slice(0, this.PAGE_SIZE);
	}
}
